import axios from '@/utils/axiosInstance';
import { BASE_URL } from '@/config/constants';

/**
 * One contact "unit" — an optional name (group title on the website) plus an
 * ORDERED list of fields. The array order is the render order; the admin
 * console reorders by dragging rows inside the unit.
 */
export interface UnitFieldData {
  type: UnitField;
  value: string;
}

export interface ContactUnit {
  name: string;
  fields: UnitFieldData[];
}

export const UNIT_FIELDS = ['phone', 'email', 'address', 'whatsapp'] as const;
export type UnitField = (typeof UNIT_FIELDS)[number];

export interface ContactInfo {
  domain_slug?: string;
  /** Info-block title on the website — empty string = default "Contact Information" */
  heading: string;
  description: string;
  units: ContactUnit[];
  showInfo: boolean;
  /** Show/hide the enquiry form on the website (inactive also rejects submissions) */
  showForm: boolean;
}

export const MAX_CONTACT_UNITS = 8;
export const MAX_FIELDS_PER_UNIT = 4;
export const MAX_HEADING_LENGTH = 80;
export const MAX_DESCRIPTION_LENGTH = 300;
export const MAX_UNIT_FIELD_LENGTH = 200;
export const MAX_UNIT_NAME_LENGTH = 60;

/** Website fallback when `heading` is empty — mirrors backend DEFAULT_CONTACT_HEADING. */
export const DEFAULT_CONTACT_HEADING = 'Contact Information';

export const emptyUnit = (): ContactUnit => ({ name: '', fields: [] });

export const getContactInfo = async (): Promise<ContactInfo> => {
  const response = await axios.get<ContactInfo>('/api/contact-info');
  return response.data;
};

export const updateContactInfo = async (payload: {
  heading?: string;
  description?: string;
  units?: ContactUnit[];
  showInfo?: boolean;
  showForm?: boolean;
}): Promise<ContactInfo> => {
  const response = await axios.put<ContactInfo>('/api/contact-info', payload);
  return response.data;
};

/**
 * Set / clear the enquiry email (where new-lead notification emails go).
 * Empty string clears the override — notifications fall back to the account email.
 */
export const updateLeadNotificationEmail = async (email: string): Promise<{ leadNotificationEmail: string | null; message: string }> => {
  const response = await axios.put<{ leadNotificationEmail: string | null; message: string }>(
    '/api/domain/lead-notification-email',
    { email: email.trim() },
    { baseURL: BASE_URL }
  );
  return response.data;
};
